import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * Separator — a thin divider between sections, menu groups, or list items.
 * Design adopted from the TailGrids separator model (the system our Figma is
 * built on), wired to First Bite tokens (1px `gray-200` line).
 *
 *   orientation  horizontal | vertical
 *   decorative   boolean   purely visual → removed from the a11y tree
 *
 * Horizontal stretches to its container's width; vertical stretches to its
 * height (use inside a flex row — it `self-stretch`es). When the divider carries
 * meaning (e.g. between two distinct regions) leave `decorative` off so it keeps
 * `role="separator"`; when it's only visual chrome, set `decorative`.
 */
export interface SeparatorProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: 'horizontal' | 'vertical'
  /** Purely visual — drops `role="separator"` so AT skips it. */
  decorative?: boolean
}

export const Separator = React.forwardRef<HTMLDivElement, SeparatorProps>(
  (
    { className, orientation = 'horizontal', decorative = false, ...props },
    ref
  ) => {
    const isVertical = orientation === 'vertical'
    return (
      <div
        ref={ref}
        role={decorative ? 'none' : 'separator'}
        aria-orientation={decorative ? undefined : orientation}
        className={cn(
          'shrink-0 bg-fbui-gray-200',
          isVertical ? 'h-full w-px self-stretch' : 'h-px w-full',
          className
        )}
        {...props}
      />
    )
  }
)
Separator.displayName = 'Separator'
